/* ==========================================================================
   NZS-ATC — the one reliable add-to-cart path (Blake 2026-07-31)

   WHY THIS EXISTS — the root cause of "add to cart doesn't work":
   The card button carried a variant id baked into the HTML at render time
   (`data-vid` = selected_or_first_available_variant.id) and the handler was:

       fetch('/cart/add.js', ...).then(r => { if (!r.ok) throw 0; ... })
                                 .catch(() => openQuickView(btn));

   Every failure mode collapsed into that one silent catch:
     - 422, because the baked id went stale (Shopify serves collection pages
       from a full-page CDN cache, so the id can be minutes-to-hours old and
       the variant may have sold out, or the product's first-available variant
       may have moved),
     - 429, because Shopify rate-limits bursts of cart writes and nothing
       serialised concurrent clicks,
     - a network blip or a bot-challenge HTML body served in place of JSON,
       which also fails `r.ok` / `r.json()`.
   Nothing retried, nothing re-resolved the id, and nothing told the customer.
   The button just appeared to do nothing. That is the whole bug.

   WHAT THIS DOES INSTEAD
     1. Serialises every add through one promise chain — concurrent clicks
        queue instead of racing into a 429.
     2. Retries 429s directly. For network errors, timeouts, 5xx and malformed
        responses it reconciles against /cart.js before any replay.
     3. On 422, re-resolves the live variant from /products/<handle>.js and
        retries once with a fresh id. This is what makes a stale cached page
        self-heal.
     4. Falls back to the quick-view picker only when a human genuinely has to
        choose (multi-variant product, or truly out of stock).
     5. Surfaces a real message on the button when it still cannot add, so a
        failure is never silent again.

   Adds go through window.fetch, so the existing NZS-ATC-FEEDBACK hook (toast,
   badge, Added state) and nzs-fly-to-cart keep working untouched.
   ========================================================================== */
(function () {
  'use strict';
  if (window.nzsATC) return;

  /* NZS-ATC-HEADROOM (Blake 2026-08-01): a Cloudflare 1015 storm answers 429 with
     Retry-After ~5s -- under the old 7.5s deadline ONE such wait consumed the whole budget and
     the add surfaced as failed even though the next attempt would have landed. More tries, a
     longer deadline, and a tighter cap on how long any single Retry-After may park us, so more
     attempts fit inside the window. The queue wait grows to match: an add queued behind a slow
     one must outlive that add's whole deadline. */
  var MAX_TRIES = 5;
  var BASE_DELAY = 250;
  var ADD_DEADLINE = 16000;
  var QUEUE_WAIT_TIMEOUT = 18000;
  var REFRESH_TIMEOUT = 1000;
  var MAX_RETRY_AFTER = 4000;
  var MAX_QUEUE = 8;
  var chain = Promise.resolve();
  var queued = 0;

  function error(message, code, status, cause) {
    var e = new Error(message);
    e.code = code;
    if (status != null) e.status = status;
    if (cause) e.cause = cause;
    return e;
  }

  function sleep(ms, ctx) {
    var left = ctx.deadline - Date.now();
    if (left <= ms) return Promise.reject(error('Timed out', 'DEADLINE'));
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function copyOpts(opts) {
    var out = {};
    Object.keys(opts || {}).forEach(function (key) { out[key] = opts[key]; });
    return out;
  }

  /* Engine writes run while holding the passive guard's queue lock. This marker
     bypasses only re-queueing; the existing feedback/fly observers still see fetch. */
  function fetchT(url, opts, ctx) {
    opts = copyOpts(opts);
    if (ctx.deadline - Date.now() <= 0) {
      return Promise.reject(error('Timed out', 'DEADLINE'));
    }
    if (opts.method && String(opts.method).toUpperCase() === 'POST') {
      opts.__nzsCartGuardBypass = true;
      if (ctx.guardFence) opts.__nzsCartGuardFence = ctx.guardFence;
    }
    /* Never abort a cart write to enforce a client timer. The server may commit
       after abort(), so an internally aborted request can never be replayed safely. */
    return window.fetch(url, opts);
  }

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  /* Malformed and unreadable responses are ambiguous: the write may have committed. */
  function readBody(res) {
    return res.text().then(function (raw) {
      try { return { json: JSON.parse(raw), parsed: true, raw: raw }; }
      catch (e) { return { json: null, parsed: false, raw: raw }; }
    }, function () {
      return { json: null, parsed: false, raw: '' };
    });
  }

  function describe(data) {
    if (!data) return '';
    if (typeof data.description === 'string' && data.description) return data.description;
    if (typeof data.message === 'string' && data.message) return data.message;
    var errs = data.errors;
    if (typeof errs === 'string') return errs;
    if (errs && typeof errs === 'object') {
      return Object.keys(errs).map(function (k) { return [].concat(errs[k]).join(', '); }).join(', ');
    }
    return '';
  }

  function isFormData(value) {
    return typeof FormData !== 'undefined' && value instanceof FormData;
  }

  function isSearchParams(value) {
    return typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams;
  }

  function isJsonPayload(value) {
    return value && typeof value === 'object' && !Array.isArray(value) &&
      !isFormData(value) && !isSearchParams(value);
  }

  function bodyInit(payload) {
    var headers = { Accept: 'application/json' };
    var body = payload;
    if (isJsonPayload(payload)) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(payload);
    } else if (!isFormData(payload) && !isSearchParams(payload)) {
      throw error("Couldn't add to cart", 'INVALID_PAYLOAD');
    }
    return { headers: headers, body: body };
  }

  function postAdd(payload, ctx) {
    var init = bodyInit(payload);
    return fetchT(root() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: init.headers,
      body: init.body
    }, ctx);
  }

  /* A 422 lookup is always cache-busted; repeating a cached product response would
     simply reproduce the stale variant that caused the failure. */
  function liveVariant(handle, wantedId, ctx) {
    if (!handle) return Promise.resolve(null);
    ctx = ctx || { deadline: Date.now() + ADD_DEADLINE };
    var url = root() + 'products/' + encodeURIComponent(handle) +
      '.js?_nzs_atc=' + Date.now();
    return fetchT(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }, ctx).then(function (res) {
      if (!res.ok) return { __failed: true };
      return res.json().catch(function () { return { __failed: true }; });
    }).catch(function (cause) {
      if (cause && cause.name === 'AbortError') throw cause;
      return { __failed: true };
    }).then(function (product) {
      if (!product || product.__failed || !product.variants) return { lookupFailed: true };
      var variants = product.variants;
      var firstFree = variants.filter(function (variant) { return variant.available; })[0];
      if (!firstFree) return { product: product, soldOut: true };
      if (variants.length > 1) return { product: product, needsChoice: true };
      return {
        id: firstFree.id,
        product: product,
        changed: String(firstFree.id) !== String(wantedId)
      };
    });
  }

  function field(payload, name) {
    if (isFormData(payload) || isSearchParams(payload)) return payload.get(name);
    return payload && payload[name];
  }

  function payloadItems(payload) {
    if (isJsonPayload(payload) && Array.isArray(payload.items)) return payload.items;
    if (isFormData(payload) || isSearchParams(payload)) {
      var indexed = {};
      payload.forEach(function (value, key) {
        var match = String(key).match(/^items\[(\d+)\]\[(id|quantity)\]$/);
        if (!match) return;
        indexed[match[1]] = indexed[match[1]] || {};
        indexed[match[1]][match[2]] = value;
      });
      var indexes = Object.keys(indexed).sort(function (a, b) { return Number(a) - Number(b); });
      if (indexes.length) return indexes.map(function (index) { return indexed[index]; });
    }
    return [{ id: field(payload, 'id'), quantity: field(payload, 'quantity') }];
  }

  function requested(payload) {
    var items = payloadItems(payload);
    var out = {};
    items.forEach(function (item) {
      var id = item && item.id;
      if (id == null || id === '') return;
      var quantity = parseInt(item.quantity, 10);
      if (!quantity || quantity < 1) quantity = 1;
      out[String(id)] = (out[String(id)] || 0) + quantity;
    });
    if (!Object.keys(out).length) throw error("Couldn't add to cart", 'INVALID_PAYLOAD');
    return out;
  }

  function quantities(cart) {
    var out = {};
    (cart.items || []).forEach(function (item) {
      var id = item.variant_id != null ? item.variant_id : item.id;
      out[String(id)] = (out[String(id)] || 0) + (parseInt(item.quantity, 10) || 0);
    });
    return out;
  }

  function getCart(ctx) {
    return fetchT(root() + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }, ctx).then(function (res) {
      if (!res.ok) throw error("Couldn't check cart", 'CART_LOOKUP_FAILED', res.status);
      return res.json().catch(function (cause) {
        throw error("Couldn't check cart", 'CART_LOOKUP_FAILED', res.status, cause);
      });
    }).then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) {
        throw error("Couldn't check cart", 'CART_LOOKUP_FAILED');
      }
      return { cart: cart, map: quantities(cart) };
    });
  }

  function compare(before, after, target) {
    var ids = Object.keys(target);
    var exact = ids.every(function (id) {
      return (after[id] || 0) - (before[id] || 0) === target[id];
    });
    if (exact) return 'committed';
    var unchanged = ids.every(function (id) {
      return (after[id] || 0) === (before[id] || 0);
    });
    return unchanged ? 'unchanged' : 'different';
  }

  function refreshUI(cart) {
    if (typeof document === 'undefined' || typeof CustomEvent !== 'function') return;
    ['cart:refresh', 'cart:build', 'cart:updated'].forEach(function (name) {
      document.dispatchEvent(new CustomEvent(name, { detail: { cart: cart } }));
    });
  }

  function emitSuccess(cart, meta) { /* NZS-CDR-SPEED-0922: forward gift transaction ownership. */
    if (typeof document === 'undefined' || typeof CustomEvent !== 'function') return;
    document.dispatchEvent(new CustomEvent('nzs:cart-added', {
      detail: { cart: cart, reconciled: true, cdrLink: meta && meta.cdrLink } /* NZS-CDR-SPEED-0922 */
    }));
  }

  function emitError(e) {
    if (typeof document === 'undefined' || typeof CustomEvent !== 'function') return;
    document.dispatchEvent(new CustomEvent('nzs:cart-error', {
      detail: {
        message: e.message || "Couldn't add to cart",
        code: e.code,
        status: e.status
      }
    }));
  }

  function retryAfter(res, tries) {
    var raw = res.headers && res.headers.get('Retry-After');
    var ms = Number(raw) * 1000;
    if (!raw || !isFinite(ms)) {
      var at = Date.parse(raw || '');
      ms = isNaN(at) ? 0 : at - Date.now();
    }
    if (!(ms > 0)) ms = BASE_DELAY * Math.pow(2, tries - 1);
    return Math.min(ms, MAX_RETRY_AFTER);
  }

  function replaceId(payload, id) {
    if (isFormData(payload)) {
      var fd = new FormData();
      payload.forEach(function (value, key) { fd.append(key, value); });
      var fdKeys = [];
      fd.forEach(function (value, key) {
        if (/^items\[\d+\]\[id\]$/.test(key)) fdKeys.push(key);
      });
      fd.set(fdKeys.length === 1 ? fdKeys[0] : 'id', String(id));
      return fd;
    }
    if (isSearchParams(payload)) {
      var params = new URLSearchParams(payload);
      var paramKeys = [];
      params.forEach(function (value, key) {
        if (/^items\[\d+\]\[id\]$/.test(key)) paramKeys.push(key);
      });
      params.set(paramKeys.length === 1 ? paramKeys[0] : 'id', String(id));
      return params;
    }
    var out = {};
    Object.keys(payload).forEach(function (key) { out[key] = payload[key]; });
    if (Array.isArray(payload.items) && payload.items.length === 1) {
      var item = {};
      Object.keys(payload.items[0]).forEach(function (key) { item[key] = payload.items[0][key]; });
      item.id = id;
      out.items = [item];
    } else {
      out.id = id;
    }
    return out;
  }

  function attempt(originalPayload, meta, ctx) {
    var payload = originalPayload;
    var items = payloadItems(payload);
    var handles = meta && Array.isArray(meta.handles) ? meta.handles : [];
    var handle = meta && meta.handle || handles[0];
    var isBatch = items.length > 1;
    var target = requested(payload);
    var wantedIds = Object.keys(target);
    var tries = 0;
    var reResolved = false;
    var snapshot;

    function reconcile(cause) {
      /* NZS-CDR-SPEED-0922: without a fresh pre-write snapshot, never infer a delta or replay.
         Refresh the visible cart on ambiguity, then ask the shopper to check it. */
      if (!snapshot) {
        return getCart(ctx).then(function (actual) {
          refreshUI(actual.cart);
          document.dispatchEvent(new CustomEvent('nzs:cdr-added-cart', { detail: { cart: actual.cart, open: true } }));
          throw error("Couldn't confirm the add. Check your cart.", 'UNCONFIRMED', null, cause);
        });
      }
      return getCart(ctx).then(function (actual) {
        var state = compare(snapshot, actual.map, target);
        if (state === 'committed') {
          refreshUI(actual.cart);
          emitSuccess(actual.cart, meta); /* NZS-CDR-SPEED-0922 */
          return { reconciled: true, cart: actual.cart };
        }
        if (state === 'unchanged') {
          snapshot = actual.map;
          if (tries < MAX_TRIES) {
            return sleep(BASE_DELAY * Math.pow(2, tries - 1), ctx).then(once);
          }
          throw error("Couldn't add to cart", 'FAILED', cause && cause.status, cause);
        }
        refreshUI(actual.cart);
        throw error("Couldn't confirm the add. Check your cart.", 'UNCONFIRMED', null, cause);
      }, function (lookupError) {
        throw error("Couldn't confirm the add. Check your cart.", 'UNCONFIRMED', null, lookupError);
      });
    }

    function once() {
      tries++;
      return postAdd(payload, ctx).then(function (res) {
        return readBody(res).then(function (body) {
          if (res.ok && body.parsed) {
            /* NZS-CDR-SPEED-0922: one post-add snapshot and one notification for engine adds.
               Preserve the add return value; a failed read must never replay a confirmed add. */
            return getCart(ctx).then(function (actual) {
              emitSuccess(actual.cart, meta); /* NZS-CDR-SPEED-0922 */
              return body.json;
            }, function () {
              emitSuccess(null, meta); /* NZS-CDR-SPEED-0922 */ // Let the drawer's sequenced read recover missing cart state.
              return body.json;
            });
          }

          if (res.status === 429) {
            if (tries < MAX_TRIES) return sleep(retryAfter(res, tries), ctx).then(once);
            /* out of tries -- but an EARLIER ambiguous attempt in this same add may have
               committed. Ask the cart before declaring failure; reconcile still throws
               FAILED when the item genuinely is not there. */
            return reconcile(error(describe(body.json) || "Couldn't add to cart", 'FAILED', 429));
          }

          if (res.status === 422) {
            if (isBatch) {
              throw error(describe(body.json) || "Couldn't add to cart", 'UNAVAILABLE', 422);
            }
            if (handle && !isBatch && !reResolved) {
              reResolved = true;
              return liveVariant(handle, wantedIds[0], ctx).then(function (info) {
                if (info && info.changed && info.id != null) {
                  payload = replaceId(payload, info.id);
                  target = requested(payload);
                  tries = 0;
                  return once();
                }
                if (info && info.lookupFailed) {
                  throw error("Couldn't check stock", 'LOOKUP_FAILED', 422);
                }
                throw error(
                  describe(body.json) || (info && info.soldOut ? 'Sold out' : "Couldn't add to cart"),
                  info && info.needsChoice ? 'NEEDS_CHOICE' : 'UNAVAILABLE',
                  422
                );
              });
            }
            throw error(describe(body.json) || "Couldn't add to cart", 'UNAVAILABLE', 422);
          }

          if (res.status >= 400 && res.status < 500) {
            throw error(describe(body.json) || "Couldn't add to cart", 'FAILED', res.status);
          }

          return reconcile(error(
            describe(body.json) || "Couldn't confirm the add",
            'AMBIGUOUS',
            res.status
          ));
        });
      }, function (cause) {
        if (cause && cause.name === 'AbortError') throw cause;
        return reconcile(cause);
      });
    }

    /* NZS-CDR-SPEED-0922: D3 sends add immediately. A cached cart is not a safe replay baseline. */
    snapshot = null;
    return once();
  }

  function refreshAfterDeadline() {
    var ctx = { deadline: Date.now() + REFRESH_TIMEOUT };
    var lookup = getCart(ctx).then(function (actual) {
      refreshUI(actual.cart);
    }, function () {});
    return new Promise(function (resolve) {
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve();
      };
      var timer = setTimeout(finish, REFRESH_TIMEOUT);
      lookup.then(finish, finish);
    });
  }

  function withAddDeadline(promise, ctx) {
    return new Promise(function (resolve, reject) {
      var timedOut = false;
      var timer = setTimeout(function () {
        timedOut = true;
        refreshAfterDeadline().then(function () {
          reject(error("Couldn't confirm the add. Check your cart.", 'UNCONFIRMED'));
        });
      }, Math.max(0, ctx.deadline - Date.now()));
      promise.then(function (value) {
        if (timedOut) return;
        clearTimeout(timer);
        resolve(value);
      }, function (cause) {
        if (timedOut) return;
        clearTimeout(timer);
        reject(cause);
      });
    });
  }

  /* NZS-PDP-0925c: meta.quiet = the caller shows the failure inline, so no global "Cart update failed" toast. */
  function settle(promise, meta) {
    return promise.then(function (value) {
      queued--;
      return value;
    }, function (e) {
      queued--;
      if (!(e && (e.name === 'AbortError' || e.code === 'NEEDS_CHOICE' || e.silent)) && !(meta && meta.quiet)) { /* NZS-PDP-0925c */
        emitError(e || error("Couldn't add to cart", 'FAILED'));
      }
      throw e;
    });
  }

  /* Payload is the caller's complete add body. Handle is separate metadata, so
     it is never leaked into or reconstructed as the Shopify request body. */
  function add(payload, meta) {
    if (!meta && isJsonPayload(payload) && payload.handle) {
      meta = { handle: payload.handle };
      var clean = {};
      Object.keys(payload).forEach(function (key) {
        if (key !== 'handle') clean[key] = payload[key];
      });
      payload = clean;
    }
    meta = meta || {};
    if (queued >= MAX_QUEUE) {
      var full = error('Cart is busy. Try again.', 'QUEUE_FULL');
      if (!meta.quiet) emitError(full); /* NZS-PDP-0925c */
      return Promise.reject(full);
    }
    queued++;
    var expired = false;
    var queueTimer;
    var queueWait = new Promise(function (resolve, reject) {
      queueTimer = setTimeout(function () {
        expired = true;
        reject(error('Cart is busy, try again.', 'QUEUE_TIMEOUT'));
      }, QUEUE_WAIT_TIMEOUT);
    });
    var run = function (guardFence) {
      if (expired) return Promise.reject(error('Cart is busy, try again.', 'QUEUE_TIMEOUT'));
      clearTimeout(queueTimer);
      var ctx = { deadline: Date.now() + ADD_DEADLINE, guardFence: guardFence };
      return withAddDeadline(attempt(payload, meta, ctx), ctx);
    };
    var guard = window.nzsCartGuard;
    var scheduled;
    if (guard && typeof guard.run === 'function') {
      scheduled = guard.run(run);
    } else {
      scheduled = chain.then(run, run);
    }
    chain = scheduled;
    return settle(Promise.race([scheduled, queueWait]), meta); /* NZS-PDP-0925c */
  }

  window.nzsATC = {
    add: add,
    liveVariant: liveVariant,
    _chain: function () { return chain; }
  };
  /* NZS-ROUND4-A1-READY: wake card delegates waiting on the deferred engine. */
  try {
    if (typeof CustomEvent === 'function') {
      document.dispatchEvent(new CustomEvent('nzs:atc-ready'));
    }
  } catch (_) {}
})();
